/**
 * Carrier Change Timeline Component
 *
 * Displays carrier information changes (name, address, officers, fleet size, ...)
 * as a minimal list grouped by day, with suspicious changes flagged inline.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';

interface CarrierChange {
  id: number;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  change_type: string;
  change_detected_at: string;
  detected_by: string;
  data_source: string;
  is_significant: boolean;
  change_category: string;
  suspicious_change: boolean;
  suspicion_reason: string | null;
}

interface ChangesByCategory {
  identity: CarrierChange[];
  location: CarrierChange[];
  contact: CarrierChange[];
  officers: CarrierChange[];
  status: CarrierChange[];
  fleet_size: CarrierChange[];
  bulk_change: CarrierChange[];
  other: CarrierChange[];
}

interface RiskAssessment {
  hasFrequentChanges: boolean;
  hasLocationChanges: boolean;
  hasOfficerChanges: boolean;
  hasBulkChanges: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface MCS150Snapshot {
  id?: string;
  snapshot_date: string;
  power_units?: number;
  drivers?: number;
  mcs_150_mileage?: string;
  [key: string]: unknown;
}

interface HistoricStats {
  dotNumber: string;
  yearsOfData?: number;
  historicalSnapshots: MCS150Snapshot[];
  summary?: {
    totalChanges: number;
    significantChanges: number;
  };
}

interface CarrierChangeTimelineProps {
  dotNumber: number;
  changes: CarrierChange[];
  changesByCategory: ChangesByCategory;
  riskAssessment: RiskAssessment;
  summary: {
    totalChanges: number;
    suspiciousChanges: number;
    significantChanges: number;
    firstChangeDate: string | null;
    lastChangeDate: string | null;
  };
  historicStats?: HistoricStats;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  legal_name: 'Legal Name',
  dba_name: 'DBA Name',
  phy_street: 'Physical Street',
  phy_city: 'Physical City',
  phy_state: 'Physical State',
  phone: 'Phone Number',
  email_address: 'Email Address',
  company_officer_1: 'Primary Officer',
  company_officer_2: 'Secondary Officer',
  status_code: 'Status',
  power_units: 'Power Units',
  total_drivers: 'Total Drivers',
  _summary: 'Bulk Changes'
};

const RISK_COLORS: Record<RiskAssessment['riskLevel'], string> = {
  HIGH: 'var(--color-danger)',
  MEDIUM: 'var(--color-warning)',
  LOW: 'var(--color-success)'
};

const formatCategory = (category: string): string =>
  category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

const formatDay = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const formatTime = (dateString: string): string =>
  new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export default function CarrierChangeTimeline({
  dotNumber,
  changes,
  changesByCategory,
  riskAssessment,
  summary,
  historicStats,
  onClose
}: CarrierChangeTimelineProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredChanges = selectedCategory
    ? changesByCategory[selectedCategory as keyof ChangesByCategory] || []
    : changes;

  // Group consecutive changes by calendar day (changes arrive newest first)
  const changesByDay: { day: string; items: CarrierChange[] }[] = [];
  filteredChanges.forEach(change => {
    const day = formatDay(change.change_detected_at);
    const last = changesByDay[changesByDay.length - 1];
    if (last && last.day === day) {
      last.items.push(change);
    } else {
      changesByDay.push({ day, items: [change] });
    }
  });

  const riskReasons = [
    riskAssessment.hasFrequentChanges && 'multiple identity changes',
    riskAssessment.hasLocationChanges && 'multiple location changes',
    riskAssessment.hasOfficerChanges && 'officer changes',
    riskAssessment.hasBulkChanges && 'bulk changes'
  ].filter(Boolean) as string[];

  const snapshots = historicStats?.historicalSnapshots ?? [];
  const hasSnapshots = snapshots.length > 1;

  const subtitleParts = [
    `DOT ${dotNumber}`,
    `${summary.totalChanges} changes`,
    summary.suspiciousChanges > 0 ? `${summary.suspiciousChanges} suspicious` : null,
    summary.firstChangeDate ? `since ${formatDay(summary.firstChangeDate)}` : null
  ].filter(Boolean) as string[];

  const categoryTabs: { key: string | null; label: string; count: number }[] = [
    { key: null, label: 'All', count: changes.length },
    ...Object.entries(changesByCategory)
      .filter(([, categoryChanges]) => categoryChanges.length > 0)
      .map(([category, categoryChanges]) => ({
        key: category,
        label: formatCategory(category),
        count: categoryChanges.length
      }))
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35,33,29,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: "var(--z-modal)",
        padding: 'var(--space-md)'
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Change history for DOT ${dotNumber}`}
        style={{
          background: '#fbf9f4',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '760px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-lg) var(--space-lg) var(--space-md)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-md)',
          flexShrink: 0
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)' }}>
              Change history
            </h2>
            <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
              {subtitleParts.join(' · ')}
            </p>
            {riskAssessment.riskLevel !== 'LOW' && (
              <p style={{ margin: 'var(--space-sm) 0 0', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                <span style={{ color: RISK_COLORS[riskAssessment.riskLevel], fontWeight: 600 }}>
                  {riskAssessment.riskLevel === 'HIGH' ? 'High risk' : 'Medium risk'}
                </span>
                {riskReasons.length > 0 && ` — ${riskReasons.join(', ')}`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--color-gray-400)',
              display: 'flex'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Category tabs */}
        <div style={{
          padding: '0 var(--space-lg)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          gap: 'var(--space-lg)',
          overflowX: 'auto',
          flexShrink: 0
        }}>
          {categoryTabs.map(tab => {
            const isActive = selectedCategory === tab.key;
            return (
              <button
                key={tab.key ?? 'all'}
                onClick={() => setSelectedCategory(tab.key)}
                style={{
                  padding: 'var(--space-sm) 0',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${isActive ? 'var(--color-primary)' : 'transparent'}`,
                  marginBottom: '-1px',
                  color: isActive ? 'var(--color-text)' : 'var(--color-text-muted)',
                  fontSize: '0.875rem',
                  fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
                <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)', fontWeight: 400 }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-sm) var(--space-lg) var(--space-lg)' }}>
          {/* MCS-150 Census Changes */}
          {hasSnapshots && (() => {
            // Keep current, oldest, and any snapshot whose values differ from the one before it
            const mcsChanges = snapshots.filter((snapshot, index: number) => {
              if (index === 0 || index === snapshots.length - 1) return true;
              const next = snapshots[index + 1];
              return (snapshot.power_units || 0) !== (next?.power_units || 0)
                || (snapshot.drivers || 0) !== (next?.drivers || 0)
                || (snapshot.mcs_150_mileage || 0) !== (next?.mcs_150_mileage || 0);
            });

            const cellStyle: React.CSSProperties = { padding: '10px 0', textAlign: 'right' };
            const deltaStyle = (delta: number): React.CSSProperties => ({
              marginLeft: '6px',
              fontSize: '0.75rem',
              color: delta > 0 ? 'var(--color-success)' : 'var(--color-danger)'
            });

            return (
              <section style={{ marginTop: 'var(--space-md)' }}>
                <div style={sectionLabelStyle}>MCS-150 census</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', color: 'var(--color-text)' }}>
                  <thead>
                    <tr style={{ color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)' }}>
                      <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>Date</th>
                      <th style={{ ...cellStyle, fontWeight: 500 }}>Power units</th>
                      <th style={{ ...cellStyle, fontWeight: 500 }}>Drivers</th>
                      <th style={{ ...cellStyle, fontWeight: 500 }}>Mileage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mcsChanges.map((snapshot, idx: number) => {
                      const origIndex = snapshots.indexOf(snapshot);
                      const prev = origIndex < snapshots.length - 1 ? snapshots[origIndex + 1] : null;
                      const powerChange = prev ? (snapshot.power_units || 0) - (prev.power_units || 0) : 0;
                      const driverChange = prev ? (snapshot.drivers || 0) - (prev.drivers || 0) : 0;

                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                          <td style={{ ...cellStyle, textAlign: 'left' }}>
                            {new Date(snapshot.snapshot_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                            {idx === 0 && <span style={{ marginLeft: '6px', color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>current</span>}
                          </td>
                          <td style={cellStyle}>
                            {snapshot.power_units || 0}
                            {powerChange !== 0 && <span style={deltaStyle(powerChange)}>{powerChange > 0 ? '+' : ''}{powerChange}</span>}
                          </td>
                          <td style={cellStyle}>
                            {snapshot.drivers || 0}
                            {driverChange !== 0 && <span style={deltaStyle(driverChange)}>{driverChange > 0 ? '+' : ''}{driverChange}</span>}
                          </td>
                          <td style={{ ...cellStyle, color: 'var(--color-text-muted)' }}>
                            {snapshot.mcs_150_mileage ? `${(parseInt(snapshot.mcs_150_mileage) / 1000000).toFixed(1)}M` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            );
          })()}

          {/* Carrier information changes, grouped by day */}
          {filteredChanges.length === 0 && !hasSnapshots ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl) 0', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
              <div style={{ color: 'var(--color-text)', fontWeight: 500 }}>No changes recorded yet</div>
              <div style={{ marginTop: '4px' }}>
                Changes are tracked automatically when carrier information is updated.
              </div>
            </div>
          ) : (
            changesByDay.map(group => (
              <section key={group.day} style={{ marginTop: 'var(--space-md)' }}>
                <div style={sectionLabelStyle}>{group.day}</div>
                {group.items.map(change => (
                  <ChangeRow key={change.id} change={change} />
                ))}
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-gray-400)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: 'var(--space-sm) 0'
};

function ChangeRow({ change }: { change: CarrierChange }) {
  const flag = change.suspicious_change
    ? { label: 'Suspicious', color: 'var(--color-danger)' }
    : change.is_significant
      ? { label: 'Significant', color: 'var(--color-warning)' }
      : null;

  return (
    <div style={{
      padding: '12px 0',
      borderTop: '1px solid var(--color-gray-100)',
      fontSize: '0.875rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
        <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>
          {FIELD_LABELS[change.field_name] || change.field_name}
        </span>
        {flag && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.75rem', color: flag.color }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: flag.color }} />
            {flag.label}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--color-gray-400)', whiteSpace: 'nowrap' }}>
          {formatTime(change.change_detected_at)}
        </span>
      </div>

      {change.field_name !== '_summary' && (
        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px', overflowWrap: 'anywhere' }}>
          <span style={{ color: 'var(--color-gray-400)' }}>{change.old_value || '(empty)'}</span>
          <span style={{ color: 'var(--color-gray-300)' }}>→</span>
          <span style={{ color: 'var(--color-text)' }}>{change.new_value || '(empty)'}</span>
        </div>
      )}

      {change.suspicion_reason && (
        <div style={{ marginTop: '4px', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
          {change.suspicion_reason}
        </div>
      )}
    </div>
  );
}
